import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router'
import { Toaster } from 'sonner'
import { I18nProvider } from '@/i18n/i18n'
import { authClient } from '@/lib/auth-client'
import { Board } from '@/pages/Board'
import { GalleryPreview } from '@/pages/GalleryPreview'
import { Boards } from '@/pages/Boards'
import { Privacy, Terms } from '@/pages/Legal'
import { Prepare } from '@/pages/Prepare'
import { Handoff } from '@/pages/Handoff'
import { Settings } from '@/pages/Settings'
import { SignIn } from '@/pages/SignIn'
import '@/styles/index.css'

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
})

function RequireSession() {
  const session = authClient.useSession()
  if (session.isPending) return null
  if (!session.data) return <SignIn />
  return <Outlet />
}

const router = createBrowserRouter([
  {
    element: <RequireSession />,
    children: [
      { path: '/', element: <Boards /> },
      { path: '/b/:id', element: <Board /> },
      { path: '/gallery/:slug', element: <GalleryPreview /> },
      { path: '/settings', element: <Settings /> },
    ],
  },
  { path: '/prepare', element: <Prepare /> },
  { path: '/upload/:token', element: <Handoff /> },
  { path: '/privacy', element: <Privacy /> },
  { path: '/terms', element: <Terms /> },
  { path: '*', element: <Navigate to="/" replace /> },
])

const root = document.getElementById('root')
if (!root) throw new Error('#root missing')

createRoot(root).render(
  <StrictMode>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster
          position="bottom-center"
          toastOptions={{
            className:
              '!rounded-lg !border-line !bg-surface !text-ink !shadow-lg !text-[13px] !font-sans',
          }}
        />
      </QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
)
